<template>
    <v-container fluid class="b-container">
      
        <v-row>
            <v-col cols="12">
                <v-skeleton-loader
                    v-if="loading"
                    type="image"
                    height="112"
                ></v-skeleton-loader>

                <div v-else class="py-6 page-card">
                    <v-row
                        no-gutters
                        justify="space-between"
                        align="center"
                        class="px-5"
                    >
                        <v-col cols="auto" class="d-flex align-center">
                            <v-icon
                                class="active-icon mr-2 error--text"
                                :class="{'success--text': company.active}"
                                color="black"
                                v-text="icons.mdiCheckboxBlankCircle"
                            ></v-icon>
                            <h4
                                v-if="company.active"
                                class="subtitle-1 success--text"
                                v-text="translations.online[lang]"
                            ></h4>
                            <h4
                                v-else
                                class="subtitle-1 error--text"
                                v-text="translations.offline[lang]"
                            ></h4>
                        </v-col>

                        <v-col
                            v-for="statistic in titleStatistics"
                            :key="statistic.text['en']"
                            cols="auto"
                        >
                            <v-card-title class="pa-0 justify-center">
                                {{ statistic.value }}
                            </v-card-title>
                            <v-row
                                no-gutters
                                align="center"
                                class="subtitle-1 text-center"
                            >
                                <!-- <v-icon
                                    v-text="statistic.icon"
                                    class="mr-1"
                                ></v-icon> -->
                                {{ statistic.text[lang] }}
                            </v-row>
                        </v-col>
                    </v-row>
                </div>
            </v-col>
            <v-col
                v-for="view in views"
                :key="view.text['en']"
                cols="12"
                sm="6"
                lg="3"
            >
                <v-skeleton-loader
                    v-if="loading"
                    type="image"
                    height="100"
                ></v-skeleton-loader>

                <div v-else tile outlined class="pa-5 page-card">
                    <v-row no-gutters align="center" class="minHeight">
                        <v-col cols="4">
                            <v-avatar color="accent" size="48">
                                <v-icon dark large v-text="view.icon"></v-icon>
                            </v-avatar>
                        </v-col>

                        <v-col cols="8" class="text-center">
                            <h1
                                class="font-weight-bold"
                                v-text="view.value"
                            ></h1>
                        </v-col>
                        <v-col cols="12" class="text-center">
                            <h4
                                class="subtitle-1 font-weight-medium"
                                v-text="view.title[lang]"
                            ></h4>
                        </v-col>
                    </v-row>
                </div>
            </v-col>

            <v-col
                v-for="transaction in transactions"
                :key="transaction.text['en']"
                cols="12"
                md="6"
            >
                <v-skeleton-loader
                    v-if="loading"
                    type="image"
                    height="100"
                ></v-skeleton-loader>

                <div v-else tile outlined class="pa-5 page-card">
                    <v-row no-gutters align="center">
                        <v-col cols="auto">
                            <v-avatar size="90">
                                <v-icon
                                    class="big-icon"
                                    color="black"
                                    large
                                    v-text="transaction.icon"
                                ></v-icon>
                            </v-avatar>
                        </v-col>

                        <v-col cols="auto">
                            <h3
                                class="font-weight-bold"
                                v-text="transaction.text[lang]"
                            ></h3>
                        </v-col>
                        <v-col cols="12" class="pl-6">
                            <h4
                                class="subtitle-1 font-weight-medium"
                                v-text="transaction.value"
                            ></h4>
                        </v-col>
                    </v-row>
                </div>
            </v-col>

            <v-col cols="12">
                <v-skeleton-loader
                    v-if="loading"
                    type="image"
                ></v-skeleton-loader>

                <div v-else class="page-card">
                    <v-card-title
                        v-text="translations.transactions[lang]"
                    ></v-card-title>

                    <v-divider></v-divider>

                    <v-card-text>
                        <canvas ref="myChart"></canvas>
                    </v-card-text>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import {
    mdiAccountGroup,
    mdiDatabase,
    mdiCurrencyEur,
    mdiBinoculars,
    mdiAccount,
    mdiKeyboardReturn,
    mdiCheckboxBlankCircle,
    mdiBasket,
    mdiLabel,
    mdiAlarm,
} from "@mdi/js";

import translations from "@/utils/translations/home";
import { mapActions, mapState } from "vuex";
import moment from "moment";
import Chart from "chart.js";

const gradients = [
    ["#222"],
    ["#42b3f4"],
    ["red", "orange", "yellow"],
    ["purple", "violet"],
    ["#00c6ff", "#F0F", "#FF0"],
    ["#f72047", "#ffd200", "#1feaea"],
];

export default {
    name: "Home",

    mixins: [translations],

    data: () => ({
        labels: [],
        values: [],
        icons: { mdiCheckboxBlankCircle },
    }),

    async mounted() {
        this.getHomePageData();
        setTimeout(() => this.createChart(), 3000);
    },

    computed: {
        ...mapState("loyaltyPanel", ["statistics", "homeData", "company"]),
        ...mapState(["permissions", "superUser"]),

        lang() {
            return this.$route.params.lang;
        },

        loading() {
            return this.$store.state.loyaltyPanel.loading;
        },

        paymentError() {
            return this.$store.state.loyaltyPanel.paymentError;
        },

        permission() {
            return this.$store.state.permissions.homepage
                ? this.$store.state.permissions.homepage.read
                : this.superUser;
        },

        titleStatistics() {
            return [
                {
                    icon: mdiAccountGroup,
                    text: this.translations.clients,
                    value: this.statistics.total_customers,
                },
                {
                    icon: mdiDatabase,
                    text: this.translations.transactions,
                    value: this.statistics.total_transactions,
                },
                {
                    icon: mdiCurrencyEur,
                    text: this.translations.total,
                    value: new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                    }).format(this.statistics.total_income),
                },
                // {
                //     icon: mdiTrophyVariant,
                //     text: {
                //         el: "Σύνολο πόντων",
                //         en: "Total Points",
                //         it: "Punti totali",
                //     },
                //     value: this.statistics.total_points,
                // },
            ];
        },

        views() {
            return [
                {
                    icon: mdiKeyboardReturn,
                    title: this.translations.loyaltyRetention,
                    text: this.translations.views,
                    value: this.homeData[0]
                        ? `${(100 * this.homeData[0].retention_rate).toFixed(2)}%`
                        : "",
                },
                {
                    icon: mdiAccount,
                    title: this.translations.customerRetention,
                    text: this.translations.clicks,
                    value: this.homeData[0]
                        ? `${(this.homeData[0].customer_retention_rate).toFixed(2)}%`
                        : "",
                },
                {
                    icon: mdiLabel,
                    title: this.translations.repeatPurchase,
                    text: this.translations.payments,
                    value: this.homeData[0]
                        ? `${(100*this.homeData[0].repeat_purchase_rate).toFixed(2)}%`
                        : "",
                },
                {
                    icon: mdiBasket,
                    title: this.translations.averageBasket,
                    text: this.translations.mapViews,
                    value: this.homeData[0]
                        ? (new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                    }).format(this.homeData[0].average_basket_value)
                   )
                        : "",
                },
            ];
        },

        transactions() {
            return [
                {
                    icon: mdiAlarm,
                    text: this.translations.last12h,
                    value: `
                        ${
                            this.statistics.last_twelve_hours_payments
                                ? this.statistics.last_twelve_hours_payments
                                      .total_transactions
                                : ""
                        }
                        ${this.translations.transactions[this.lang]} /
                        ${new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "EUR",
                            minimumFractionDigits: 2,
                        }).format(
                            this.statistics.last_twelve_hours_payments
                                ? this.statistics.last_twelve_hours_payments
                                      .total_price
                                : ""
                        )}
                        ${this.translations.total[this.lang]}
                    `,
                },
                {
                    icon: mdiAlarm,
                    text: this.translations.last7d,
                    value: `
                        ${
                            this.statistics.last_week_payments
                                ? this.statistics.last_week_payments
                                      .total_transactions
                                : ""
                        }
                        ${this.translations.transactions[this.lang]} /
                        ${new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "EUR",
                            minimumFractionDigits: 2,
                        }).format(
                            this.statistics.last_week_payments
                                ? this.statistics.last_week_payments.total_price
                                : ""
                        )}
                        ${this.translations.total[this.lang]}
                    `,
                },
            ];
        },
    },
    watch: {
        permission(val) {
            if (!val) {
                this.$router.replace(
                    `/${this.lang}/storePanel/forbidden-gateway`
                );
            }
        },

        ["$store.state.loyaltyPanel.statistics"]: {
            immediate: true,
            handler(val) {
                if (val.last_seven_days_revenue) {
                    if (!this.labels.length) {
                        val.last_seven_days_revenue.forEach((r) =>
                            this.labels.push(
                                moment(r.date).format("DD/MM/YYYY")
                            )
                        );

                        this.labels.reverse();
                    }

                    if (!this.values.length) {
                        val.last_seven_days_revenue.forEach((r) =>
                            this.values.push(r.total_price)
                        );

                        this.values.reverse();
                    }
                }
            },
        },
        ["company.store_id"]: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.getStatistics();
                }
            },
        },
    },
    methods: {
        ...mapActions("loyaltyPanel", ["getStatistics", "getHomePageData"]),

        createChart() {
            if (!this.paymentError && this.$refs.myChart) {
                let ctx = this.$refs.myChart.getContext("2d");

                var gradientStroke = ctx.createLinearGradient(
                    500,
                    -100,
                    100,
                    0
                );
                gradientStroke.addColorStop(0, "#1feaea");
                gradientStroke.addColorStop(0.33, "yellow");
                gradientStroke.addColorStop(0.66, "orange");
                gradientStroke.addColorStop(1, "red");
                // console.log(this.values, "this.values");
                var myChart = new Chart(ctx, {
                    type: "line",
                    data: {
                        labels: this.labels,
                        datasets: [
                            {
                                label: "",
                                data: this.values,
                                backgroundColor: ["rgba(0, 0, 0, 0)"],
                                borderColor: gradientStroke,
                                pointBorderColor: gradientStroke,
                                pointBackgroundColor: gradientStroke,
                                pointHoverBackgroundColor: gradientStroke,
                                pointHoverBorderColor: gradientStroke,
                                borderWidth: 4,
                            },
                        ],
                    },
                    options: {
                        legend: {
                            position: "top",
                        },
                        scales: {
                            yAxes: [
                                {
                                    ticks: {
                                        fontColor: "rgba(0,0,0,0.5)",
                                        fontStyle: "bold",
                                        beginAtZero: true,
                                        maxTicksLimit: 5,
                                        padding: 20,
                                    },
                                    gridLines: {
                                        drawTicks: false,
                                    },
                                },
                            ],
                            xAxes: [
                                {
                                    gridLines: {
                                        zeroLineColor: "transparent",
                                    },
                                    ticks: {
                                        padding: 20,
                                        fontColor: "rgba(0,0,0,0.5)",
                                        fontStyle: "bold",
                                    },
                                },
                            ],
                        },
                    },
                });
            }
        },
    },
};
</script>
<style scoped>
.minHeight {
    min-height: 120px;
}

.page-card {
    background-color: white;
    border-radius: 10px;
}

.big-icon {
    width: 50px !important;
    height: 50px !important;
}

.active-icon {
    width: 10px !important;
    height: 10px !important;
}
</style>
